import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import axios from 'axios';

import { axiosOrgInstance, makeRequest } from './middleware';
import type { AuthData } from './store-interfaces';
import type { AdminSubjectAccessToken } from './store-interfaces';
import { jwtDecode } from 'jwt-decode';
import router from '@/router';
import adminRouter from '@/router/admin-router';
import lmsRouter from '@/router/lms-router';
import { useOrganizationStore } from './organization';
import { useLMSStore } from './lms';
import { getCPRolesFromToken } from './helpers';
import { notifyTokenChanged } from './token-signal';
import { getEnv } from '@/config/projectEnvVariables';
import { clearAllIncidentFormDrafts } from '@/incidents/forms/draft-storage';
import { THEME_MODE_STORAGE_KEY } from './theme';
import { FEATURE_FLAGS } from '@/config/featureFlags';

// Device-level preferences that must survive the localStorage wipe on logout —
// they belong to the browser, not to the authenticated session.
const PERSISTENT_PREFERENCE_KEYS = [
    THEME_MODE_STORAGE_KEY,
    ...Object.values(FEATURE_FLAGS).map((flag) => flag.storageKey),
];

/** Largest delay setTimeout honours (2^31 - 1 ms); larger values fire immediately. */
const MAX_TIMER_DELAY_MS = 2_147_483_647;

export const useAuthStore = defineStore('auth', () => {
    const organizationStore = useOrganizationStore();
    const lmsStore = useLMSStore();

    // We use localStorage to keep the data persistent
    const isAuthenticated = ref(localStorage.getItem('isAuthenticated') === 'true');
    const impersonationActive = ref(localStorage.getItem('impersonationActive') === 'true');
    const activeImpersonationSession = ref(localStorage.getItem('activeImpersonationSession') ?? '');
    const logoutInProgress = ref(false);
    let refreshTimer: ReturnType<typeof setTimeout> | null = null;
    let sessionInvalidationInProgress = false;
    if (localStorage.getItem('isAuthenticated') === 'true') {
        scheduleTokenRefresh();
    }

    // Functions

    function resolveCurrentRouter() {
        const isLMSModeEnabled = JSON.parse(localStorage.getItem('lms') ?? 'false');
        return isLMSModeEnabled ? lmsRouter : localStorage.getItem('admin') === 'true' ? adminRouter : router;
    }

    async function getToken() {
        return localStorage.getItem('token');
    }

    function normalizeLoginRedirect(redirectPath?: string) {
        if (!redirectPath || !redirectPath.startsWith('/') || redirectPath.startsWith('//')) {
            return '/';
        }

        return redirectPath;
    }

    async function finalizeLogin(data: AuthData, redirectPath?: string) {
        populateLocalStorage(data);
        const roles = getCPRolesFromToken(data.access_token);
        if (['admin', 'cdim-admin', 'noc-admin'].some((role) => roles.includes(role))) {
            localStorage.setItem('admin', 'true');
        } else {
            localStorage.setItem('admin', 'false');
        }
        await lmsStore.setup();
        // Fall back to a path saved by the org-router catch-all guard for routes that only
        // exist in the admin router (e.g. /users), which isn't mounted until after login.
        const pendingRedirect = sessionStorage.getItem('pendingRedirect') ?? undefined;
        sessionStorage.removeItem('pendingRedirect');
        await resolveCurrentRouter()
            .push(normalizeLoginRedirect(redirectPath ?? pendingRedirect))
            .then(() => {
                setTimeout(() => {
                    window.location.reload();
                }, 1000);
            });
    }

    async function login(username: string, password: string, redirectPath?: string) {
        const response = await axios.post(`${getEnv().VITE_ORGANIZATION_API_HOST}/auth-management/login`, {
            username: username,
            password: password,
        });

        // TODO: Handle login errors better
        if (response.data.code >= 400 || response.data.success === false) {
            return response;
        }
        await finalizeLogin(response.data.data as AuthData, redirectPath);
        return response;
    }

    async function getSsoAuthorizationUrl(email: string, redirectUri: string, state: string) {
        return await axios.get(`${getEnv().VITE_ORGANIZATION_API_HOST}/auth-management/sso/authorize`, {
            params: {
                redirect_uri: redirectUri,
                state,
                login_hint: email,
            },
        });
    }

    async function ssoExchange(code: string, redirectUri: string, redirectPath?: string) {
        const response = await axios.post(`${getEnv().VITE_ORGANIZATION_API_HOST}/auth-management/sso/exchange`, {
            code,
            redirect_uri: redirectUri,
        });
        if (response.data.code >= 400 || response.data.success === false) {
            return response;
        }
        await finalizeLogin(response.data.data as AuthData, redirectPath);
        return response;
    }

    type StoredSession = {
        accessToken: string;
        refreshToken: string;
        tokenExpires: string;
        admin: string;
        lms: string;
        organizationId: string;
    };

    function getCurrentSession(): StoredSession {
        return {
            accessToken: localStorage.getItem('token') ?? '',
            refreshToken: localStorage.getItem('refreshToken') ?? '',
            tokenExpires: localStorage.getItem('tokenExpires') ?? '',
            admin: localStorage.getItem('admin') ?? 'false',
            lms: localStorage.getItem('lms') ?? 'false',
            organizationId: localStorage.getItem('organization_id') ?? '',
        };
    }

    function storeSession(key: string, session: StoredSession) {
        localStorage.setItem(key, JSON.stringify(session));
    }

    function getStoredSession(key: string) {
        const rawSession = localStorage.getItem(key);
        if (!rawSession) return undefined;

        try {
            return JSON.parse(rawSession) as StoredSession;
        } catch {
            return undefined;
        }
    }

    function getTokenLabel(accessToken: string) {
        try {
            const decodedToken = jwtDecode(accessToken) as Partial<{
                name: string;
                given_name: string;
                family_name: string;
                email: string;
                preferred_username: string;
                sub: string;
            }>;
            const fullName =
                decodedToken.name ||
                [decodedToken.given_name, decodedToken.family_name].filter(Boolean).join(' ').trim();

            return (
                fullName || decodedToken.email || decodedToken.preferred_username || decodedToken.sub || 'Unknown user'
            );
        } catch {
            return 'Unknown user';
        }
    }

    function applyStoredSession(session: StoredSession) {
        localStorage.setItem('isAuthenticated', true.toString());
        localStorage.setItem('token', session.accessToken);
        localStorage.setItem('refreshToken', session.refreshToken);
        localStorage.setItem('tokenExpires', session.tokenExpires);
        localStorage.setItem('admin', session.admin);
        localStorage.setItem('lms', session.lms);

        if (session.organizationId) {
            localStorage.setItem('organization_id', session.organizationId);
        }

        isAuthenticated.value = true;
        notifyTokenChanged();
        scheduleTokenRefresh();
    }

    const impersonationSessions = computed(() => {
        if (!impersonationActive.value) return undefined;

        const originalSession = getStoredSession('impersonationOriginalSession');
        const subjectSession = getStoredSession('impersonationSubjectSession');
        if (!originalSession || !subjectSession) return undefined;

        return {
            active: activeImpersonationSession.value,
            original: {
                label: getTokenLabel(originalSession.accessToken),
            },
            subject: {
                label: getTokenLabel(subjectSession.accessToken),
            },
        };
    });

    async function switchImpersonationSession(target: 'original' | 'subject') {
        const sessionKey = target === 'original' ? 'impersonationOriginalSession' : 'impersonationSubjectSession';
        const session = getStoredSession(sessionKey);
        if (!session) return;

        activeImpersonationSession.value = target;
        localStorage.setItem('activeImpersonationSession', target);
        applyStoredSession(session);

        window.location.assign('/');
    }

    async function endImpersonation() {
        const originalSession = getStoredSession('impersonationOriginalSession');
        if (!originalSession) return;

        impersonationActive.value = false;
        activeImpersonationSession.value = '';
        localStorage.removeItem('impersonationActive');
        localStorage.removeItem('activeImpersonationSession');
        localStorage.removeItem('impersonationOriginalSession');
        localStorage.removeItem('impersonationSubjectSession');

        applyStoredSession(originalSession);
        window.location.assign('/');
    }

    async function startImpersonation(token: AdminSubjectAccessToken) {
        if (!impersonationActive.value) {
            storeSession('impersonationOriginalSession', getCurrentSession());
        }

        const subjectAuthData = {
            access_token: token.accessToken,
            refresh_token: token.refreshToken,
            expires_in: token.expiresIn,
            refresh_expires_in: token.refreshExpiresIn,
            token_type: token.tokenType,
            scope: token.scope,
            not_before_policy: 0,
            session_state: '',
        };

        storeSession('impersonationSubjectSession', {
            accessToken: subjectAuthData.access_token,
            refreshToken: subjectAuthData.refresh_token,
            tokenExpires: jwtDecode(subjectAuthData.access_token).exp?.toString() || '',
            admin: getCPRolesFromToken(subjectAuthData.access_token).some((role) =>
                ['admin', 'cdim-admin', 'noc-admin'].includes(role),
            )
                ? 'true'
                : 'false',
            lms: 'false',
            organizationId: localStorage.getItem('organization_id') ?? '',
        });
        impersonationActive.value = true;
        activeImpersonationSession.value = 'subject';
        localStorage.setItem('impersonationActive', 'true');
        localStorage.setItem('activeImpersonationSession', 'subject');

        await finalizeLogin(subjectAuthData);
    }

    async function logout() {
        logoutInProgress.value = true;
        const currentRouter = resolveCurrentRouter();
        const refreshToken = localStorage.getItem('refreshToken');

        const finishLogout = async () => {
            clearStoredSession();
            await currentRouter.push({ name: 'login' });
            // TODO: Remove this delay. It is a workaround for the current logout flow clearing auth state
            // before router/app-shell state fully settles, which can cause a brief inconsistent transition.
            // The proper fix is to make logout a normal SPA transition with explicit store reset and routing.
            setTimeout(() => {
                // Guard against firing after a jsdom test env tears down — without it, this 1s timer
                // throws "window is not defined" as an unhandled error and fails the test run.
                if (typeof window !== 'undefined') {
                    window.location.reload();
                }
            }, 1000);
        };

        try {
            if (!refreshToken) {
                await finishLogout();
                return;
            }

            const response = await makeRequest('post', '/auth-management/logout', axiosOrgInstance, {
                refresh_token: refreshToken,
            });

            if (response.status === 200 || response.status === 204) {
                await finishLogout();
            } else {
                console.error('Failed to logout', response);
            }
        } finally {
            logoutInProgress.value = false;
        }
    }

    async function resetPass(email: string) {
        // because the global reset is inside org-api scope
        const axiosOrg = axios.create({
            baseURL: getEnv().VITE_ORGANIZATION_API_HOST,
        });
        let success = false;
        try {
            await axiosOrg
                .put('/auth-management/reset/link', {
                    email: email,
                })
                .then((response) => {
                    if (response.status !== 204) {
                        useOrganizationStore().setToastMessage('Failed to send password reset link', 'warning');
                    } else {
                        useOrganizationStore().setToastMessage('Password reset link sent to the email', 'check');
                        success = true;
                    }
                });
        } catch (error: unknown) {
            console.error(error);
        }
        return success;
    }

    async function updatePass(newPassword: string, token: string) {
        try {
            const axiosOrg = axios.create({
                baseURL: getEnv().VITE_ORGANIZATION_API_HOST,
            });
            axiosOrg
                .put('/auth-management/reset/password', {
                    password: newPassword,
                    token: token,
                })
                .then((response) => {
                    if (response.status === 204) {
                        useOrganizationStore().setToastMessage('Password successfully updated', 'check');
                        resolveCurrentRouter().push('/login');
                    } else {
                        console.error('Failed to update password', response);
                        useOrganizationStore().setToastMessage('Failed to update password', 'warning');
                    }
                });
        } catch (error) {
            console.error('Error updating password', error);
            useOrganizationStore().setToastMessage('Failed to update password', 'warning');
        }
    }

    function populateLocalStorage(data: AuthData, update: boolean = false) {
        try {
            if (!update) {
                localStorage.setItem('isAuthenticated', true.toString());
                isAuthenticated.value = true;
                //   localStorage.setItem("admin", "false");
            }
            localStorage.setItem('token', data.access_token || '');
            localStorage.setItem('refreshToken', data.refresh_token || '');
            localStorage.setItem('tokenExpires', jwtDecode(data.access_token).exp?.toString() || '');
            if (impersonationActive.value) {
                const sessionKey =
                    activeImpersonationSession.value === 'original'
                        ? 'impersonationOriginalSession'
                        : 'impersonationSubjectSession';
                storeSession(sessionKey, getCurrentSession());
            }
            notifyTokenChanged();
            scheduleTokenRefresh();
        } catch (error) {
            console.error('failed to populate local storage', error);
        }
    }

    function scheduleTokenRefresh() {
        if (refreshTimer) clearTimeout(refreshTimer);

        const exp = Number(localStorage.getItem('tokenExpires'));
        if (!exp) return;
        // console.log("token exp", new Date(exp * 1000).toISOString());
        const expiresInMs = exp * 1000 - Date.now();
        // console.log("token expires in s", Math.floor(expiresInMs / 1000));
        // Browsers keep timer delays in a signed 32-bit int; anything past 2^31 - 1 ms (24.8 days)
        // fires at once, which would refresh (and on failure expire) a perfectly valid session.
        // Wait the maximum and re-check when it elapses.
        const refreshInMs = Math.min(Math.max(expiresInMs - 10 * 60_000, 0), MAX_TIMER_DELAY_MS);
        // console.log("refresh in s", Math.floor(refreshInMs / 1000));
        refreshTimer = setTimeout(async () => {
            if (refreshInMs === MAX_TIMER_DELAY_MS && expiresInMs - 10 * 60_000 > MAX_TIMER_DELAY_MS) {
                scheduleTokenRefresh();
                return;
            }
            await refreshToken();
        }, refreshInMs);
    }

    function clearStoredSession() {
        if (refreshTimer) {
            clearTimeout(refreshTimer);
            refreshTimer = null;
        }

        isAuthenticated.value = false;
        clearAllIncidentFormDrafts();
        const preservedPreferences = PERSISTENT_PREFERENCE_KEYS.flatMap((key) => {
            const value = localStorage.getItem(key);
            return value === null ? [] : [[key, value] as const];
        });
        localStorage.clear();
        for (const [key, value] of preservedPreferences) {
            localStorage.setItem(key, value);
        }
        organizationStore.usersOrganization = undefined;
        organizationStore.availableOrganizations = [];
    }

    async function expireSession(message: string = 'Session expired, please log in again') {
        if (sessionInvalidationInProgress) {
            return;
        }

        sessionInvalidationInProgress = true;
        const currentRouter = resolveCurrentRouter();
        clearStoredSession();
        organizationStore.setToastMessage(message, 'warning');

        await currentRouter.replace({ name: 'login' }).then(() => {
            setTimeout(() => {
                window.location.reload();
            }, 1000);
        });
    }

    async function refreshToken() {
        // TODO

        let refreshResponse:
            | {
                  data: {
                      data: AuthData;
                  };
              }
            | undefined;
        const expiration = localStorage.getItem('tokenExpires');

        if (!localStorage.getItem('refreshToken') || (expiration && Number(expiration) * 1000 > Date.now())) {
            try {
                refreshResponse = (await axios.post(`${getEnv().VITE_ORGANIZATION_API_HOST}/auth-management/refresh`, {
                    refresh_token: localStorage.getItem('refreshToken'),
                })) as { data: { data: AuthData } };
                populateLocalStorage(refreshResponse.data.data as AuthData, true);
            } catch (error) {
                console.log('refresh token failed', error);
                await expireSession();
            }
        } else {
            await expireSession();
        }
        return refreshResponse;
    }

    return {
        // Refs
        isAuthenticated,
        impersonationActive,
        impersonationSessions,
        logoutInProgress,
        // Functions
        login,
        getSsoAuthorizationUrl,
        ssoExchange,
        startImpersonation,
        switchImpersonationSession,
        endImpersonation,
        logout,
        getToken,
        refreshToken,
        expireSession,
        resetPass,
        updatePass,
    };
});
